// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import '../styles/skeleton.css';

interface SkeletonLoaderProps {
  variant?: 'table' | 'card' | 'form' | 'detail';
  rows?: number;
  count?: number;
  label?: string;
}

export function SkeletonLoader({
  variant,
  rows = 5,
  count = 3,
  label = 'Loading...',
}: SkeletonLoaderProps) {
  const srOnly: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0,
  };

  if (!variant) {
    return (
      <div
        className="dm-page skeleton-loader"
        role="status"
        aria-busy="true"
        data-testid="skeleton-loader"
        style={{ padding: '2rem' }}
      >
        <span style={srOnly}>{label}</span>
        <div
          className="skeleton-pulse"
          style={{ height: '2rem', width: '40%', marginBottom: '1rem' }}
        />
        <div
          className="skeleton-pulse"
          style={{ height: '1rem', width: '70%', marginBottom: '0.75rem' }}
        />
        <div
          className="skeleton-pulse"
          style={{ height: '1rem', width: '55%', marginBottom: '0.75rem' }}
        />
        <div className="skeleton-pulse" style={{ height: '1rem', width: '60%' }} />
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div
        className="dm-skeleton-table skeleton-loader"
        role="status"
        aria-busy="true"
        data-testid="skeleton-loader"
      >
        <span style={srOnly}>{label}</span>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="dm-skeleton-row">
            <div className="skeleton-pulse" style={{ width: '25%', height: '16px' }} />
            <div className="skeleton-pulse" style={{ width: '30%', height: '16px' }} />
            <div className="skeleton-pulse" style={{ width: '20%', height: '16px' }} />
            <div className="skeleton-pulse" style={{ width: '15%', height: '16px' }} />
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className="dm-skeleton-cards skeleton-loader"
        role="status"
        aria-busy="true"
        data-testid="skeleton-loader"
      >
        <span style={srOnly}>{label}</span>
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="dm-skeleton-card">
            <div
              className="skeleton-pulse"
              style={{ width: '60%', height: '20px', marginBottom: '12px' }}
            />
            <div
              className="skeleton-pulse"
              style={{ width: '100%', height: '14px', marginBottom: '8px' }}
            />
            <div
              className="skeleton-pulse"
              style={{ width: '80%', height: '14px', marginBottom: '8px' }}
            />
            <div
              className="skeleton-pulse"
              style={{ width: '40%', height: '32px', marginTop: '16px' }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'detail') {
    return (
      <div
        className="dm-skeleton-detail skeleton-loader"
        role="status"
        aria-busy="true"
        data-testid="skeleton-loader"
      >
        <span style={srOnly}>{label}</span>
        <div className="dm-skeleton-detail-header">
          <div
            className="skeleton-pulse"
            style={{ width: '50%', height: '28px', marginBottom: '0.75rem' }}
          />
        </div>
        <div className="dm-skeleton-detail-meta">
          <div className="skeleton-pulse" style={{ width: '15%', height: '14px' }} />
          <div className="skeleton-pulse" style={{ width: '20%', height: '14px' }} />
        </div>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="dm-skeleton-detail-row">
            <div className="skeleton-pulse" style={{ width: '20%', height: '14px' }} />
            <div className="skeleton-pulse" style={{ width: '45%', height: '14px' }} />
          </div>
        ))}
        <div
          className="skeleton-pulse"
          style={{ width: '80%', height: '60px', marginTop: '1rem' }}
        />
      </div>
    );
  }

  // variant === 'form'
  return (
    <div
      className="dm-skeleton-form skeleton-loader"
      role="status"
      aria-busy="true"
      data-testid="skeleton-loader"
    >
      <span style={srOnly}>{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="dm-skeleton-field">
          <div
            className="skeleton-pulse"
            style={{ width: '30%', height: '14px', marginBottom: '6px' }}
          />
          <div className="skeleton-pulse" style={{ width: '100%', height: '36px' }} />
        </div>
      ))}
    </div>
  );
}
